import type { SkillCategory } from '@applicator/shared'
import type { SupabaseClient } from '@supabase/supabase-js'

import { RESUME_SECTIONS } from '../components/ResumeDetail/constants'
import { sortSkillsInCategories } from '../components/ResumeDetail/skillsEditorUtils'

const SKILLS_DISPLAY_ORDER = RESUME_SECTIONS.findIndex((section) => section.type === 'skills')

export function skillsHasContent(categories: SkillCategory[]): boolean {
  if (!Array.isArray(categories) || categories.length === 0) return false

  return categories.some((category) => {
    const hasName = String(category.name ?? '').trim().length > 0
    const hasSkills = (category.skills ?? []).some(
      (skill) => String(skill.name ?? '').trim().length > 0,
    )
    return hasName || hasSkills
  })
}

function parseSkillsContent(raw: unknown): SkillCategory[] | null {
  let categories: unknown = raw
  if (
    raw &&
    typeof raw === 'object' &&
    !Array.isArray(raw) &&
    Array.isArray((raw as { categories?: unknown }).categories)
  ) {
    categories = (raw as { categories: unknown[] }).categories
  }
  if (!Array.isArray(categories)) return null
  const cloned = cloneSkillsCategories(categories as SkillCategory[])
  return skillsHasContent(cloned) ? cloned : null
}

export function cloneSkillsCategories(categories: SkillCategory[]): SkillCategory[] {
  return categories.map((category) => ({
    ...category,
    skills: (category.skills ?? []).map((skill) => ({ ...skill })),
  }))
}

/** Catalog fingerprint: category/skill ids and names only (order-sensitive). */
export function normalizeSkillsCatalogFingerprint(categories: SkillCategory[]): string {
  const normalized = categories.map((category) => ({
    id: category.id,
    name: category.name,
    skills: [...(category.skills ?? [])]
      .sort((a, b) => a.id.localeCompare(b.id))
      .map((skill) => ({ id: skill.id, name: skill.name })),
  }))
  normalized.sort((a, b) => a.id.localeCompare(b.id))
  return JSON.stringify(normalized)
}

export function isSkillsCatalogEqual(a: SkillCategory[], b: SkillCategory[]): boolean {
  return normalizeSkillsCatalogFingerprint(a) === normalizeSkillsCatalogFingerprint(b)
}

/** Union account + resume catalogs so resume-only skills survive reload before account sync. */
export function unionSkillsCatalogs(
  accountCatalog: SkillCategory[],
  resumeCatalog: SkillCategory[],
): SkillCategory[] {
  const byCategoryId = new Map<string, SkillCategory>()

  for (const category of accountCatalog) {
    byCategoryId.set(category.id, {
      ...category,
      skills: (category.skills ?? []).map((skill) => ({ ...skill })),
    })
  }

  for (const category of resumeCatalog) {
    const existing = byCategoryId.get(category.id)
    if (!existing) {
      byCategoryId.set(category.id, {
        ...category,
        skills: (category.skills ?? []).map((skill) => ({ ...skill })),
      })
      continue
    }

    const skillsById = new Map((existing.skills ?? []).map((skill) => [skill.id, skill]))
    for (const skill of category.skills ?? []) {
      if (!skillsById.has(skill.id)) {
        skillsById.set(skill.id, { ...skill })
      }
    }
    existing.skills = [...skillsById.values()]
    if (!String(existing.name ?? '').trim() && String(category.name ?? '').trim()) {
      existing.name = category.name
    }
  }

  return sortSkillsInCategories([...byCategoryId.values()])
}

export function mergeCatalogWithResumeInclusions(
  catalog: SkillCategory[],
  resumeSkills: SkillCategory[] | null | undefined,
  options: { defaultIncluded?: boolean } = {},
): SkillCategory[] {
  const { defaultIncluded = false } = options
  const inclusionById = new Map<string, boolean>()

  for (const category of resumeSkills ?? []) {
    for (const skill of category.skills ?? []) {
      if (skill.id) {
        inclusionById.set(skill.id, skill.included ?? false)
      }
    }
  }

  return sortSkillsInCategories(
    catalog.map((category) => ({
      ...category,
      skills: (category.skills ?? []).map((skill) => ({
        ...skill,
        included: inclusionById.has(skill.id)
          ? inclusionById.get(skill.id)!
          : defaultIncluded,
      })),
    })),
  )
}

/** Apply included flags from a template resume (core = checked skills on template). */
export function mergeCatalogWithTemplateCore(
  catalog: SkillCategory[],
  templateSkills: SkillCategory[] | null | undefined,
): SkillCategory[] {
  return mergeCatalogWithResumeInclusions(catalog, templateSkills, { defaultIncluded: false })
}

/** Set included on every skill in the catalog. */
export function setAllSkillsIncluded(
  catalog: SkillCategory[],
  included: boolean,
): SkillCategory[] {
  return sortSkillsInCategories(
    catalog.map((category) => ({
      ...category,
      skills: (category.skills ?? []).map((skill) => ({
        ...skill,
        included,
      })),
    })),
  )
}

export function countIncludedSkills(categories: SkillCategory[]): number {
  return categories.reduce(
    (sum, category) =>
      sum + (category.skills ?? []).filter((skill) => skill.included).length,
    0,
  )
}

/** Most recently updated resume skills catalog for the account (optional exclude). */
export async function fetchAccountSkills(
  supabase: SupabaseClient,
  options: { excludeResumeId?: string } = {},
): Promise<SkillCategory[] | null> {
  const { excludeResumeId } = options

  const {
    data: { user },
    error: authError,
  } = await supabase.auth.getUser()
  if (authError) throw authError
  if (!user) return null

  const { data: resumes, error: resumesError } = await supabase
    .from('resumes')
    .select('id, updated_at')
    .eq('user_id', user.id)
    .order('updated_at', { ascending: false })

  if (resumesError) throw resumesError

  const resumeRows = (resumes ?? []).filter((resume) => resume.id !== excludeResumeId)
  if (resumeRows.length === 0) return null

  const resumeIds = resumeRows.map((resume) => resume.id)
  const { data: contentRows, error: contentError } = await supabase
    .from('resume_content')
    .select('resume_id, content')
    .in('resume_id', resumeIds)
    .eq('section_type', 'skills')

  if (contentError) throw contentError

  const contentByResume = new Map<string, unknown>()
  for (const row of contentRows ?? []) {
    contentByResume.set(row.resume_id, row.content)
  }

  for (const resume of resumeRows) {
    const parsed = parseSkillsContent(contentByResume.get(resume.id))
    if (parsed) {
      return cloneSkillsCategories(parsed)
    }
  }

  return null
}

/** Propagate catalog structure to every resume; preserve per-resume included flags. */
export async function syncAccountSkillsCatalogToAllResumes(
  supabase: SupabaseClient,
  catalog: SkillCategory[],
  options: {
    sourceResumeId?: string
    sourceResumeSkills?: SkillCategory[]
  } = {},
): Promise<void> {
  const { sourceResumeId, sourceResumeSkills } = options

  const {
    data: { user },
    error: authError,
  } = await supabase.auth.getUser()
  if (authError) throw authError
  if (!user) return

  const { data: resumes, error: resumesError } = await supabase
    .from('resumes')
    .select('id')
    .eq('user_id', user.id)

  if (resumesError) throw resumesError

  const resumeIds = (resumes ?? []).map((resume) => resume.id)
  if (resumeIds.length === 0) return

  const { data: contentRows, error: contentError } = await supabase
    .from('resume_content')
    .select('resume_id, content')
    .in('resume_id', resumeIds)
    .eq('section_type', 'skills')

  if (contentError) throw contentError

  const skillsByResume = new Map<string, SkillCategory[]>()
  for (const row of contentRows ?? []) {
    const parsed = parseSkillsContent(row.content)
    if (parsed) {
      skillsByResume.set(row.resume_id, parsed)
    }
  }

  const catalogClone = cloneSkillsCategories(catalog)
  const rows = resumeIds.map((resumeId) => {
    let content: SkillCategory[]
    if (sourceResumeId && resumeId === sourceResumeId && sourceResumeSkills) {
      content = sortSkillsInCategories(cloneSkillsCategories(sourceResumeSkills))
    } else {
      content = mergeCatalogWithResumeInclusions(
        catalogClone,
        skillsByResume.get(resumeId) ?? [],
      )
    }

    return {
      resume_id: resumeId,
      section_type: 'skills' as const,
      content,
      display_order: SKILLS_DISPLAY_ORDER,
    }
  })

  const { error: upsertError } = await supabase.from('resume_content').upsert(rows, {
    onConflict: 'resume_id,section_type',
    ignoreDuplicates: false,
  })

  if (upsertError) throw upsertError
}
